import { useEffect, useState, useRef, memo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Button } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import BackupTableRoundedIcon from '@mui/icons-material/BackupTableRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';
import dayjs from 'dayjs';
import axios from 'axios';

var api = axios.create({ withCredentials: true });

function createToken(userName, kid, secret, iss, scp) {
    var header = {
        "alg": "HS256",
        "typ": "JWT",
        "iss": iss, //CONNECTED APP ID
        "kid": kid, //CONNECTED APP SECRET ID
    };
    var stringifiedHeader = CryptoJS.enc.Utf8.parse(JSON.stringify(header));
    var encodedHeader = base64url(stringifiedHeader);
    var claimSet = {
        "sub": userName, // THE USER NAME, like aalteirac@tableau.com 
        "aud": "tableau",
        "nbf": Math.round(new Date().getTime() / 1000) - 100,
        "jti": new Date().getTime().toString(),
        "iss": iss,
        "scp": scp.split(","),
        "exp": Math.round(new Date().getTime() / 1000) + 100
    };
    var stringifiedData = CryptoJS.enc.Utf8.parse(JSON.stringify(claimSet));
    var encodedData = base64url(stringifiedData);
    var token = encodedHeader + "." + encodedData;
    var signature = CryptoJS.HmacSHA256(token, secret); //CONNECTED APP SECRET
    signature = base64url(signature);
    var signedToken = token + "." + signature;
    return signedToken;
}

function base64url(source) {
    var encodedSource = CryptoJS.enc.Base64.stringify(source);
    encodedSource = encodedSource.replace(/=+$/, '');
    encodedSource = encodedSource.replace(/\+/g, '-');
    encodedSource = encodedSource.replace(/\//g, '_');
    return encodedSource;
}

const hours = Array.from({ length: 24 }, (_, i) => ({
    value: dayjs().hour(i).format('HH'),
    label: dayjs().hour(i).format('hh A')
}));
const minutes = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, '0'),
    label: i.toString().padStart(2, '0')
}));
const days = Array.from({ length: 28 }, (_, i) => (i + 1).toString());


const TableauViz = memo(({ dashboard, districts, warehouses, salesmen, vizRef }) => (
    <div
        style={{ background: 'url(./images/ai_logo.png) no-repeat center', height: '100%', width: '100%', padding: 0, margin: 0 }}
    >
        <tableau-viz
            id="tableauViz"
            ref={vizRef}
            src={"https://us-east-1.online.tableau.com/t/reports-ff5b5b290a/views/" + dashboard}
            token={createToken(process.env.REACT_APP_VIZ_USERNAME, process.env.REACT_APP_VIZ_KID, process.env.REACT_APP_VIZ_SECRET, process.env.REACT_APP_VIZ_ISS, process.env.REACT_APP_VIZ_SCP)}
            toolbar="hidden"
            hide-tabs
            width="100%"
            height="100%"
            style={{ width: '100%', height: '100%', border: 'none', padding: 0, margin: 0 }}
        >
            <viz-filter field="District" value={districts} ></viz-filter>
            <viz-filter field="Warehouse" value={warehouses} ></viz-filter>
            <viz-filter field="Name (Salesman)" value={salesmen} ></viz-filter>
        </tableau-viz>
    </div>
));

const Analytics = memo((props) => {
    const { isLoading, isAuthenticated, error, loginWithRedirect, getAccessTokenSilently } = useAuth0();
    const [supported, setSupported] = useState(null);
    const viz = useRef(null);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [tabValue, setTabValue] = useState('daily');
    const [dailyHour, setDailyHour] = useState('');
    const [dailyMinute, setDailyMinute] = useState('');
    const [weeklyDay, setWeeklyDay] = useState('');
    const [weeklyHour, setWeeklyHour] = useState('');
    const [weeklyMinute, setWeeklyMinute] = useState('');
    const [monthlyDay, setMonthlyDay] = useState('');
    const [monthlyHour, setMonthlyHour] = useState('');
    const [monthlyMinute, setMonthlyMinute] = useState('');
    const [existingSchedules, setExistingSchedules] = useState({ daily: false, weekly: false, monthly: false });

    const handleScheduleModalOpen = () => {
        setTabValue('daily');
        setDailyHour('');
        setDailyMinute('');
        setWeeklyDay('');
        setWeeklyHour('');
        setWeeklyMinute('');
        setMonthlyDay('');
        setMonthlyHour('');
        setMonthlyMinute('');
        setExistingSchedules({ daily: false, weekly: false, monthly: false });
        setScheduleModalOpen(true);
        fetchUserSchedules();
    };

    const handleScheduleModalClose = () => {
        setScheduleModalOpen(false);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleDailyHourChange = (event) => {
        setDailyHour(event.target.value);
    };

    const handleDailyMinuteChange = (event) => {
        setDailyMinute(event.target.value);
    };

    const handleWeeklyDayChange = (event) => {
        setWeeklyDay(event.target.value);
    };

    const handleWeeklyHourChange = (event) => {
        setWeeklyHour(event.target.value);
    };

    const handleWeeklyMinuteChange = (event) => {
        setWeeklyMinute(event.target.value);
    };

    const handleMonthlyDayChange = (event) => {
        setMonthlyDay(event.target.value);
    };

    const handleMonthlyHourChange = (event) => {
        setMonthlyHour(event.target.value);
    };

    const handleMonthlyMinuteChange = (event) => {
        setMonthlyMinute(event.target.value);
    };

    const fetchUserSchedules = () => {
        const data = { email: props.user.email };

        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.post(`${process.env.REACT_APP_BASEURL}/api/scheduler/getall`,
                data,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            ).then((response) => {
                const schedules = response.data;
                const newExistingSchedules = { daily: false, weekly: false, monthly: false };
                schedules.forEach(schedule => {
                    if (schedule.data.dashboard === props.dashboard) {
                        const [minute, hour, dayOfMonth, month, dayOfWeek] = schedule.data.schedule.split(' ');
                        if (schedule.data.type === 'daily') {
                            setDailyHour(hour);
                            setDailyMinute(minute);
                            newExistingSchedules.daily = true;
                        } else if (schedule.data.type === 'weekly') {
                            setWeeklyHour(hour);
                            setWeeklyMinute(minute);
                            setWeeklyDay(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek]);
                            newExistingSchedules.weekly = true;
                        } else if (schedule.data.type === 'monthly') {
                            setMonthlyHour(hour);
                            setMonthlyMinute(minute);
                            setMonthlyDay(dayOfMonth);
                            newExistingSchedules.monthly = true;
                        }
                    }
                });
                setExistingSchedules(newExistingSchedules);
            }).catch(error => {
                toast.error('Error fetching schedules.');
            });
        });
    };

    const handleScheduleSubmit = (type) => {
        let cronSchedule = '';
        if (type === 'daily') {
            cronSchedule = `${dailyMinute} ${dailyHour} * * *`;
        } else if (type === 'weekly') {
            const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(weeklyDay);
            cronSchedule = `${weeklyMinute} ${weeklyHour} * * ${dayOfWeek}`;
        } else if (type === 'monthly') {
            cronSchedule = `${monthlyMinute} ${monthlyHour} ${monthlyDay} * *`;
        }
        var districts = '';
        props.user.districts.forEach((district) => {
            districts += district.district + ',';
        });
        districts = districts.substr(0, districts.length - 1);

        var warehouses = '';
        props.user.warehouses.forEach((warehouse) => {
            warehouses += warehouse.warehouse + ',';
        });
        warehouses = warehouses.substr(0, warehouses.length - 1);

        var salesmen = '';
        props.user.salesmen.forEach((salesman) => {
            salesmen += salesman.salesman_name + ',';
        });
        salesmen = salesmen.substr(0, salesmen.length - 1);

        const data = {
            schedule: cronSchedule,
            warehouses: warehouses,
            districts: districts,
            email: props.user.email,
            view: props.view,
            dashboard: props.dashboard,
            title: props.title,
            type: type,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.post(`${process.env.REACT_APP_BASEURL}/api/scheduler/start`,
                data,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            ).then((response) => {
                fetchUserSchedules();
                toast.success('Schedule saved.');
            }).catch(error => {
                toast.error('Error saving schedule.');
            });
        });
    };

    const handleRemoveSchedule = (type) => {
        let cronSchedule = '';
        if (type === 'daily') {
            cronSchedule = `${dailyMinute} ${dailyHour} * * *`;
        } else if (type === 'weekly') {
            const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(weeklyDay);
            cronSchedule = `${weeklyMinute} ${weeklyHour} * * ${dayOfWeek}`;
        } else if (type === 'monthly') {
            cronSchedule = `${monthlyMinute} ${monthlyHour} ${monthlyDay} * *`;
        }

        var districts = '';
        props.user.districts.forEach((district) => {
            districts += district.district + ',';
        });
        districts = districts.substr(0, districts.length - 1);

        var warehouses = '';
        props.user.warehouses.forEach((warehouse) => {
            warehouses += warehouse.warehouse + ',';
        });
        warehouses = warehouses.substr(0, warehouses.length - 1);

        var salesmen = '';
        props.user.salesmen.forEach((salesman) => {
            salesmen += salesman.salesman_name + ',';
        });
        salesmen = salesmen.substr(0, salesmen.length - 1);

        const data = {
            schedule: cronSchedule,
            districts: districts,
            warehouses: warehouses,
            salesmen: salesmen,
            email: props.user.email,
            view: props.view,
            dashboard: props.dashboard,
            title: props.title,
            type: type,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.post(`${process.env.REACT_APP_BASEURL}/api/scheduler/delete`,
                data,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            ).then((response) => {
                toast.success('Schedule removed.');
                if (type === 'daily') {
                    setDailyHour('');
                    setDailyMinute('');
                } else if (type === 'weekly') {
                    setWeeklyHour('');
                    setWeeklyMinute('');
                    setWeeklyDay('');
                } else if (type === 'monthly') {
                    setMonthlyHour('');
                    setMonthlyMinute('');
                    setMonthlyDay('');
                }
                setExistingSchedules(prev => ({ ...prev, [type]: false }));
            }).catch(error => {
                toast.error('Error removing schedule.');
            });

        });
    };

    const reloadData = () => {
        if (viz.current) {
            try {
                viz.current.refreshDataAsync();
            } catch (err) {
                toast.error('Refresh Error');
            }
        }
    };

    const exportData = (type) => {
        if (viz.current) {
            try {
                viz.current.displayDialogAsync(type);
            } catch (err) {
                toast.error('Export Error');
            }
        }
    };

    /////////////////////

    const init = (event) => {
        setInterval(() => {
            if (viz.current) {
                viz.current.pauseAutomaticUpdatesAsync().then(() => {
                    viz.current.resumeAutomaticUpdatesAsync().catch(() => {
                        window.location.reload();
                    });
                }).catch(() => {
                    window.location.reload();
                });
            }
        }, 300000);
        viz.current.removeEventListener("firstinteractive", init);
    }

    const checkCookie = (event) => {
        if (event.data === "3pcSupported" || event.data === "3pcUnsupported") {
            setSupported(event.data === "3pcSupported");
            document.body.removeChild(document.getElementById('3pc'));
            window.removeEventListener("message", checkCookie);
        }
    }

    useEffect(() => {
        const frame = document.createElement("iframe");
        frame.id = "3pc";
        frame.src = "https://whsmacon.com/cookiecheck.html"; //Add your hosted domain url here
        frame.style.display = "none";
        frame.style.position = "fixed";
        document.body.appendChild(frame);
        window.addEventListener("message", checkCookie, false);

        if (viz.current) {
            viz.current.addEventListener("firstinteractive", init);
        }
    });

    if (isLoading) {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }
    if (error) {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                {error.message}
            </Box>
        );
    }

    if (isAuthenticated) {
        if (supported === null) {
            return (
                <Box sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography as='h1' color='primary'>Checking Browser Compatibility ...</Typography>
                </Box>
            );
        } else {
            if (!supported) {
                return (
                    <Box sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}>
                        <Typography as='h1' color='primary' gutterBottom>3rd Party Cookies Are Not Enabled</Typography>
                        <Divider flexItem sx={{ mb: 2 }} />
                        <Typography as='p'>
                            The Analytics Dashboards require 3rd Party Cookies to be enabled in your browser. This is disabled by default in Safari and most incognito or private browser windows.<br /><br />
                            For information on how to enable 3rd Party Cookies in your browser, please follow <a href='https://support.microsoft.com/en-us/office/enable-cookies-6b018d22-1d24-43d9-8543-3d35ddb2cb52' target="_blank">these instructions</a>.
                        </Typography>
                    </Box>
                );
            } else {
                var districts = '';
                props.user.districts.forEach((district) => {
                    districts += district.district + ',';
                });
                districts = districts.substr(0, districts.length - 1);

                var warehouses = '';
                props.user.warehouses.forEach((warehouse) => {
                    warehouses += warehouse.warehouse + ',';
                });
                warehouses = warehouses.substr(0, warehouses.length - 1);

                var salesmen = '';
                props.user.salesmen.forEach((salesman) => {
                    salesmen += salesman.salesman_name + ',';
                });
                salesmen = salesmen.substr(0, salesmen.length - 1);

                return (
                    <Box sx={{ width: '100%', height: '100%', position: 'relative', p: 0, m: 0 }}>
                        <TableauViz dashboard={props.dashboard} districts={districts} warehouses={warehouses} salesmen={salesmen} vizRef={viz} />
                        <SpeedDial
                            ariaLabel="SpeedDial"
                            sx={{ position: 'absolute', right: 15, top: 5 }}
                            icon={<SpeedDialIcon />}
                            direction='left'
                        >
                            <SpeedDialAction
                                key={'Reload'}
                                icon={<SyncRoundedIcon />}
                                tooltipTitle={'Reload'}
                                onClick={() => { reloadData(); }}
                            />
                            <SpeedDialAction
                                key={'Subscribe'}
                                icon={<MoreTimeRoundedIcon />}
                                tooltipTitle={'Subscribe'}
                                onClick={() => { handleScheduleModalOpen(); }}
                            />
                            <SpeedDialAction
                                key={'Export PDF'}
                                icon={<PictureAsPdfRoundedIcon />}
                                tooltipTitle={'Export PDF'}
                                onClick={() => { exportData('export-pdf'); }}
                            />
                            <SpeedDialAction
                                key={'Export CSV'}
                                icon={<BackupTableRoundedIcon />}
                                tooltipTitle={'Export CSV'}
                                onClick={() => { exportData('export-cross-tab'); }}
                            />
                        </SpeedDial>

                        <Dialog open={scheduleModalOpen} onClose={handleScheduleModalClose} fullWidth maxWidth="sm">
                            <DialogTitle>Schedule Export</DialogTitle>
                            <DialogContent>
                                <Tabs value={tabValue} onChange={handleTabChange} centered>
                                    <Tab label="Daily" value="daily" />
                                    <Tab label="Weekly" value="weekly" />
                                    <Tab label="Monthly" value="monthly" />
                                </Tabs>
                                {tabValue === 'daily' && (
                                    <Box sx={{ mt: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="daily-hour-label">Hour</InputLabel>
                                            <Select
                                                labelId="daily-hour-label"
                                                id="daily-hour"
                                                value={dailyHour}
                                                label="Hour"
                                                onChange={handleDailyHourChange}
                                            >
                                                {hours.map((hour) => (
                                                    <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel id="daily-minute-label">Minute</InputLabel>
                                            <Select
                                                labelId="daily-minute-label"
                                                id="daily-minute"
                                                value={dailyMinute}
                                                label="Minute"
                                                onChange={handleDailyMinuteChange}
                                            >
                                                {minutes.map((minute) => (
                                                    <MenuItem key={minute.value} value={minute.value}>{minute.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <DialogActions>
                                            <Button onClick={handleScheduleModalClose} variant='contained' color="primary">
                                                Close
                                            </Button>
                                            <Button onClick={() => handleScheduleSubmit('daily')} variant='contained' color="success">
                                                Save Daily Schedule
                                            </Button>
                                            {existingSchedules.daily && (
                                                <Button onClick={() => handleRemoveSchedule('daily')} variant='contained' color="error">
                                                    Remove Daily Schedule
                                                </Button>
                                            )}
                                        </DialogActions>
                                    </Box>
                                )}
                                {tabValue === 'weekly' && (
                                    <Box sx={{ mt: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="weekly-day-label">Day</InputLabel>
                                            <Select
                                                labelId="weekly-day-label"
                                                id="weekly-day"
                                                value={weeklyDay}
                                                label="Day"
                                                onChange={handleWeeklyDayChange}
                                            >
                                                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel id="weekly-hour-label">Hour</InputLabel>
                                            <Select
                                                labelId="weekly-hour-label"
                                                id="weekly-hour"
                                                value={weeklyHour}
                                                label="Hour"
                                                onChange={handleWeeklyHourChange}
                                            >
                                                {hours.map((hour) => (
                                                    <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel id="weekly-minute-label">Minute</InputLabel>
                                            <Select
                                                labelId="weekly-minute-label"
                                                id="weekly-minute"
                                                value={weeklyMinute}
                                                label="Minute"
                                                onChange={handleWeeklyMinuteChange}
                                            >
                                                {minutes.map((minute) => (
                                                    <MenuItem key={minute.value} value={minute.value}>{minute.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <DialogActions>
                                            <Button onClick={handleScheduleModalClose} variant='contained' color="primary">
                                                Close
                                            </Button>
                                            <Button onClick={() => handleScheduleSubmit('weekly')} variant='contained' color="success">
                                                Save Weekly Schedule
                                            </Button>
                                            {existingSchedules.weekly && (
                                                <Button onClick={() => handleRemoveSchedule('weekly')} variant='contained' color="error">
                                                    Remove Weekly Schedule
                                                </Button>
                                            )}
                                        </DialogActions>
                                    </Box>
                                )}
                                {tabValue === 'monthly' && (
                                    <Box sx={{ mt: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="monthly-day-label">Day</InputLabel>
                                            <Select
                                                labelId="monthly-day-label"
                                                id="monthly-day"
                                                value={monthlyDay}
                                                label="Day"
                                                onChange={handleMonthlyDayChange}
                                            >
                                                {days.map((day) => (
                                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel id="monthly-hour-label">Hour</InputLabel>
                                            <Select
                                                labelId="monthly-hour-label"
                                                id="monthly-hour"
                                                value={monthlyHour}
                                                label="Hour"
                                                onChange={handleMonthlyHourChange}
                                            >
                                                {hours.map((hour) => (
                                                    <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel id="monthly-minute-label">Minute</InputLabel>
                                            <Select
                                                labelId="monthly-minute-label"
                                                id="monthly-minute"
                                                value={monthlyMinute}
                                                label="Minute"
                                                onChange={handleMonthlyMinuteChange}
                                            >
                                                {minutes.map((minute) => (
                                                    <MenuItem key={minute.value} value={minute.value}>{minute.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <DialogActions>
                                            <Button onClick={handleScheduleModalClose} variant='contained' color="primary">
                                                Close
                                            </Button>
                                            <Button onClick={() => handleScheduleSubmit('monthly')} variant='contained' color="success">
                                                Save Monthly Schedule
                                            </Button>
                                            {existingSchedules.monthly && (
                                                <Button onClick={() => handleRemoveSchedule('monthly')} variant='contained' color="error">
                                                    Remove Monthly Schedule
                                                </Button>
                                            )}
                                        </DialogActions>
                                    </Box>
                                )}
                            </DialogContent>
                        </Dialog>
                    </Box>
                );
            }
        }
    } else {
        loginWithRedirect();
    }
});

export default Analytics;